/** Hairstyle catalog by display name; FLUX shape detail is composed on the server from styleId. */

import { isPromptStyleHidden } from './hiddenPromptStyleIds.js';

/** bald | short | medium | long — drives long→short FLUX locks on the server. */
export function targetHairLengthForStyle(style) {
  if (!style) return '';
  const cat = String(style.category || '').toLowerCase();
  const name = String(style.name || style.prompt || '').toLowerCase();
  const blob = `${cat} ${name}`;
  if (cat.includes('indian') || cat.includes('bridal')) return 'long';
  if (/\b(bald|buzz|shaved|skin fade|fade|crop|pixie|caesar|crew)\b/.test(blob)) {
    return 'short';
  }
  if (cat.includes('short')) return 'short';
  if (cat.includes('long') || cat.includes('braid') || cat.includes('ponytail') || cat.includes('man bun')) {
    return 'long';
  }
  if (cat.includes('medium') || cat.includes('lob') || cat.includes('shoulder')) return 'medium';
  if (/\b(bob|lob|wolf|shag|layers|shoulder)\b/.test(name) && !name.includes('long ')) {
    return 'medium';
  }
  if (name.includes('long')) return 'long';
  return 'medium';
}

function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Bundled CTA thumb: frontend/public/hair-style-thumbs/{styleId}.jpg */
export function localHairStyleThumbPath(styleId) {
  const id = String(styleId || '').trim();
  return id ? `/hair-style-thumbs/${id}.jpg` : '';
}

function filterVisibleGroupNames(groups, gender) {
  const seen = new Set();
  return groups
    .map((g) => ({
      ...g,
      names: g.names.filter((name) => {
        const id = `${gender}_${slugify(name)}`;
        if (isPromptStyleHidden(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      }),
    }))
    .filter((g) => g.names.length > 0);
}

function flattenGroups(groups, gender) {
  const out = [];
  for (const g of filterVisibleGroupNames(groups, gender)) {
    for (const name of g.names) {
      const base = slugify(name);
      const id = `${gender}_${base}`;
      out.push({
        id,
        name,
        prompt: name,
        category: g.category,
        thumbnail_url: localHairStyleThumbPath(id),
      });
    }
  }
  return out;
}

const WOMEN_GROUPS = [
  {
    category: 'Short Hair',
    names: [
      'Pixie Cut',
      'Textured Pixie',
      'Long Pixie',
      'French Bob',
      'Classic Bob',
      'Blunt Bob',
      'Layered Bob',
      'Inverted Bob',
      'A-Line Bob',
      'Italian Bob',
      'Shaggy Bob',
      'Curly Bob',
    ],
  },
  {
    category: 'Medium Hair',
    names: [
      'Shoulder-Length Layers',
      'Long Bob (Lob)',
      'Textured Lob',
      'Feathered Layers',
      'Face-Framing Layers',
      'Butterfly Cut',
      'Wolf Cut',
      'Shag Cut',
      'U-Cut',
      'V-Cut',
      'Step Cut',
      'Curtain-Layer Cut',
    ],
  },
  {
    category: 'Long Hair',
    names: [
      'Long Straight Layers',
      'Long U-Cut',
      'Long V-Cut',
      'Feather Cut',
      'Butterfly Layers',
      'Long Shag',
      'Mermaid Layers',
      'Face-Framing Long Layers',
      'Korean Long Layers',
      'Soft Long Layers',
    ],
  },
  {
    category: 'Curly / Wavy',
    names: [
      'Curly Bob',
      'Curly Lob',
      'Layered Curls',
      'Curly Shag',
      'Wolf Cut Curls',
      'Defined Long Curls',
      'Beach Waves',
      'Soft Waves',
      'Hollywood Waves',
      'Loose Curls',
    ],
  },
  {
    category: 'Ponytails',
    names: [
      'High Ponytail',
      'Low Ponytail',
      'Sleek Ponytail',
      'Textured Ponytail',
      'Bubble Ponytail',
      'Side Ponytail',
      'High Voluminous Ponytail',
      'Braided Ponytail',
    ],
  },
  {
    category: 'Buns / Updos',
    names: [
      'Low Bun',
      'Sleek Low Bun',
      'High Bun',
      'Messy Bun',
      'Top Knot',
      'French Twist',
      'Chignon',
      'Braided Bun',
      'Low Textured Updo',
      'Voluminous Bridal Bun',
    ],
  },
  {
    category: 'Braids',
    names: [
      'Classic Three-Strand Braid',
      'French Braid',
      'Dutch Braid',
      'Fishtail Braid',
      'Waterfall Braid',
      'Crown Braid',
      'Side Braid',
      'Rope Braid',
      'Milkmaid Braid',
      'Half-Up Braid',
    ],
  },
  {
    category: 'Indian / Bridal',
    names: [
      'Traditional South Indian Braid',
      'Long Temple Braid',
      'Bridal Braid with Flowers',
      'Jasmine-Adorned Braid',
      'Bridal Bun with Gajra',
      'South Indian Bridal Bun',
      'Braided Bridal Updo',
      'Half-Up Bridal Waves',
      'Low Bridal Chignon',
      'Classic Bridal Updo',
    ],
  },
  {
    category: 'Trendy / Modern',
    names: [
      'Curtain Bangs + Layers',
      'Wispy Bangs',
      'Birkin Bangs',
      'Korean Hush Cut',
      'Wolf Cut',
      'Butterfly Cut',
      'Octopus Cut',
      'Jellyfish Cut',
      'Italian Layers',
      'Korean Layered Cut',
      'Soft Shag',
      'Modern Mullet',
    ],
  },
];

const MEN_GROUPS = [
  {
    category: 'Short Hair',
    names: [
      'Buzz Cut',
      'Induction Cut',
      'Crew Cut',
      'Ivy League',
      'Caesar Cut',
      'French Crop',
      'Textured Crop',
      'Short Crop',
      'High and Tight',
      'Military Cut',
      'Short Spiky Hair',
      'Short Textured Hair',
      'Short Side Part',
      'Short Slick Back',
      'Short Quiff',
    ],
  },
  {
    category: 'Fade Styles',
    names: [
      'Low Fade',
      'Mid Fade',
      'High Fade',
      'Skin Fade',
      'Bald Fade',
      'Taper Fade',
      'Low Taper Fade',
      'Mid Taper Fade',
      'High Taper Fade',
      'Drop Fade',
      'Burst Fade',
      'Temple Fade',
      'Shadow Fade',
      'Skin Fade with Crop',
      'Fade with Textured Top',
    ],
  },
  {
    category: 'Medium-Length Hair',
    names: [
      'Classic Side Part',
      'Modern Side Part',
      'Textured Side Part',
      'Medium Quiff',
      'Textured Quiff',
      'Pompadour',
      'Modern Pompadour',
      'Slick Back',
      'Textured Slick Back',
      'Brushed Back',
      'Curtains',
      'Middle Part',
      'Bro Flow',
      'Messy Flow',
      'Layered Medium Cut',
      'Medium Shag',
    ],
  },
  {
    category: 'Long Hair',
    names: [
      'Long Layers',
      'Long Straight Hair',
      'Long Wavy Hair',
      'Long Curly Hair',
      'Man Bun',
      'Half-Up Man Bun',
      'Low Man Bun',
      'Top Knot',
      'Long Slick Back',
      'Shoulder-Length Flow',
      'Long Shag',
      'Wolf Cut',
    ],
  },
  {
    category: 'Curly / Wavy',
    names: [
      'Curly Crop',
      'Curly Fade',
      'Curly Taper',
      'Short Curly Top',
      'Medium Curly Layers',
      'Long Curly Layers',
      'Wavy Crop',
      'Wavy Quiff',
      'Wavy Side Part',
      'Wavy Curtains',
      'Curly Shag',
      'Textured Waves',
    ],
  },
  {
    category: 'Trendy / Modern',
    names: [
      'Wolf Cut',
      'Modern Mullet',
      'Burst Fade Mullet',
      'Textured Mullet',
      'Korean Two-Block',
      'Korean Curtain Cut',
      'Korean Comma Hair',
      'K-Pop Layered Cut',
      'Edgar Cut',
      'Textured Fringe',
      'Messy Fringe',
      'French Crop Fade',
      'Broccoli Cut',
      'Undercut',
      'Disconnected Undercut',
    ],
  },
  {
    category: 'Professional / Formal',
    names: [
      'Executive Contour',
      'Classic Taper',
      'Business Cut',
      "Gentleman's Side Part",
      'Slick Back',
      'Classic Pompadour',
      'Ivy League',
      'Comb Over',
      'Hard Part',
      'Low Taper + Side Part',
    ],
  },
  {
    category: 'Indian / Desi',
    names: [
      'Textured Indian Crop',
      'Side Swept Indian Cut',
      'Messy Indian Quiff',
      'Bollywood-Inspired Layers',
      'Long Layered Indian Hair',
      'Indian Slick Back',
      'Textured Fade',
      'Low Fade + Beard',
      'Mid Fade + Quiff',
      'Taper + Side Part',
    ],
  },
];

export function getPromptStyleGroups(gender) {
  const g = String(gender || '').toLowerCase();
  const key = g === 'men' || g === 'male' ? 'men' : 'women';
  const groups = key === 'men' ? MEN_GROUPS : WOMEN_GROUPS;
  return filterVisibleGroupNames(groups, key);
}

export function getPromptStylesForGender(gender) {
  const g = String(gender || '').toLowerCase();
  const key = g === 'men' || g === 'male' ? 'men' : 'women';
  return flattenGroups(key === 'men' ? MEN_GROUPS : WOMEN_GROUPS, key);
}

function lengthBucket(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'bald' || v === 'buzz' || v === 'shaved') return 'bald';
  if (v === 'short' || v === 'short_dense' || v === 'crop') return 'short';
  if (v === 'medium' || v === 'mid') return 'medium';
  if (v === 'long' || v === 'full') return 'long';
  return '';
}

function categoryLengthFit(category, bucket, isMen) {
  const cat = String(category || '').toLowerCase();
  if (bucket === 'bald') {
    if (isMen && (cat.includes('fade') || cat.includes('short'))) return 18;
    if (!isMen && cat.includes('short')) return 16;
    if (cat.includes('long') || cat.includes('braid') || cat.includes('bridal')) return -20;
    return 4;
  }
  if (bucket === 'short') {
    if (cat.includes('short') || cat.includes('fade')) return 14;
    if (cat.includes('long') || cat.includes('braid')) return -10;
    return 2;
  }
  if (bucket === 'medium') {
    if (cat.includes('medium') || cat.includes('lob') || cat.includes('shoulder')) return 12;
    if (cat.includes('short') && !cat.includes('medium')) return 4;
    if (cat.includes('long') && !cat.includes('medium')) return -4;
    return 6;
  }
  if (bucket === 'long') {
    if (cat.includes('long') || cat.includes('braid') || cat.includes('ponytail')) return 12;
    if (cat.includes('fade') || cat.includes('buzz')) return -14;
    return 4;
  }
  return 0;
}

function coverageFit(category, name, coverage, isMen) {
  const cov = String(coverage || 'full').toLowerCase();
  const cat = String(category || '').toLowerCase();
  const n = String(name || '').toLowerCase();
  if (cov === 'bald' || cov === 'thinning_front' || cov === 'thinning_crown') {
    if (n.includes('buzz') || n.includes('fade') || n.includes('bald') || n.includes('crop')) return 16;
    if (cat.includes('fade') || cat.includes('short')) return 12;
    if (cat.includes('long') || n.includes('layers') && n.includes('long')) return -12;
    return 0;
  }
  if (cov === 'short_dense') {
    if (cat.includes('short') || cat.includes('fade')) return 8;
    return 2;
  }
  return 4;
}

function faceShapeFit(name, category, faceShape) {
  const shape = String(faceShape || '').toLowerCase();
  if (!shape) return 0;
  const text = `${name} ${category}`.toLowerCase();
  const rules = {
    round: ['face-framing', 'layer', 'quiff', 'pompadour', 'pixie', 'lob', 'volume', 'bang'],
    square: ['layer', 'textured', 'side part', 'soft', 'wave', 'curtain', 'fringe'],
    heart: ['bob', 'face-framing', 'chin', 'bang', 'lob', 'layer'],
    oblong: ['wave', 'bang', 'layer', 'bob', 'fringe', 'curtain'],
    diamond: ['bang', 'fringe', 'curtain', 'layer', 'bob', 'side part'],
    oval: ['classic', 'layer', 'bob', 'lob'],
  };
  const keys = rules[shape] || rules.oval;
  let score = 0;
  for (const kw of keys) {
    if (text.includes(kw)) score += 3;
  }
  return score;
}

const HEAD_VIEW_LABELS = {
  front: 'front view',
  side: 'side profile',
  back: 'back view',
  three_quarter: 'angled view',
};

/** Gemma hair_profile.head_view — drives which catalog styles FLUX can render reliably. */
export function headViewFromProfile(profile) {
  const v = String(profile?.head_view || '')
    .toLowerCase()
    .replace(/-/g, '_');
  if (v === 'threequarter' || v === '3_4') return 'three_quarter';
  if (['front', 'side', 'back', 'three_quarter'].includes(v)) return v;
  return '';
}

export function headViewDisplayLabel(view) {
  return HEAD_VIEW_LABELS[view] || '';
}

function styleBlob(style) {
  return `${style.category || ''} ${style.name || ''}`.toLowerCase();
}

const BACK_UPDO_RE =
  /\b(bun|chignon|updo|top knot|man bun|french twist|milkmaid|gajra|bridal bun|braided bun|sleek low bun|messy bun|high bun|low bun|voluminous bridal|south indian bridal bun|low bridal chignon)\b/;
const BACK_BRAID_RE =
  /\b(french braid|dutch braid|fishtail braid|classic three-strand|traditional south indian braid|temple braid|rope braid|waterfall braid)\b/;
const FRONT_BANG_RE = /\b(bang|fringe|wispy bang|birkin bang|curtain bang|face-framing bang|caesar cut|french crop)\b/;

/** False = hide from catalog/recommendations for this photo angle. */
export function styleFitsHeadView(style, view) {
  if (!view || !style) return true;
  const blob = styleBlob(style);
  const cat = String(style.category || '').toLowerCase();
  const isBackUpdo = cat.includes('buns') || cat.includes('updos') || BACK_UPDO_RE.test(blob);
  const isBackBraid = cat.includes('braids') && BACK_BRAID_RE.test(blob);
  const isBackHeavy = isBackUpdo || isBackBraid;

  if (view === 'front') {
    if (isBackHeavy) return false;
    if (cat.includes('indian') && /\b(bun|braid|updo|gajra)\b/.test(blob)) return false;
    return true;
  }
  if (view === 'back') {
    if (FRONT_BANG_RE.test(blob) && !cat.includes('long') && !cat.includes('medium')) return false;
    return true;
  }
  if (view === 'side') {
    if (isBackUpdo && !/\b(side|half-up|low)\b/.test(blob)) return false;
    return true;
  }
  if (view === 'three_quarter') {
    if (isBackUpdo && cat.includes('updos')) return false;
    return true;
  }
  return true;
}

export function filterStyleGroupsForHeadView(groups, view) {
  if (!view || !Array.isArray(groups)) return groups;
  return groups
    .map((g) => ({
      ...g,
      names: g.names.filter((name) => styleFitsHeadView({ name, category: g.category }, view)),
    }))
    .filter((g) => g.names.length > 0);
}

export function filterPromptStylesForHeadView(styles, view) {
  if (!view || !Array.isArray(styles)) return styles;
  return styles.filter((s) => styleFitsHeadView(s, view));
}

function reasonForStyle(style, profile) {
  const parts = [];
  const shape = profile?.face_shape;
  const len = profile?.current_length;
  if (shape) parts.push(`${shape} face`);
  if (len && len !== 'unknown') parts.push(`${len} hair`);
  const cov = profile?.coverage;
  if (cov && cov !== 'full') parts.push(cov.replace(/_/g, ' '));
  const ctx = parts.length ? ` (${parts.join(', ')})` : '';
  return `Fits your photo${ctx} — ${style.category}`;
}

function scorePromptStyle(style, profile, isMen) {
  const bucket = lengthBucket(profile?.current_length);
  const view = headViewFromProfile(profile);
  if (view && !styleFitsHeadView(style, view)) return -1000;
  let score = categoryLengthFit(style.category, bucket, isMen);
  score += coverageFit(style.category, style.name, profile?.coverage, isMen);
  score += faceShapeFit(style.name, style.category, profile?.face_shape);
  if (profile?.coverage === 'full' && bucket === 'long' && String(style.category).toLowerCase().includes('long')) {
    score += 6;
  }
  if (view === 'back' && /\b(bun|braid|ponytail|long layers|man bun)\b/.test(styleBlob(style))) {
    score += 8;
  }
  if (
    view === 'back' &&
    String(style.category || '').toLowerCase().includes('bridal')
  ) {
    score += 10;
  }
  if (view === 'front' && /\b(bob|fringe|bang|crop|fade|layer|lob)\b/.test(styleBlob(style))) {
    score += 6;
  }
  if (view === 'side' && /\b(side part|undercut|quiff|pompadour|curtain)\b/.test(styleBlob(style))) {
    score += 6;
  }
  return score;
}

/** Rank name-only catalog styles from Gemma hair_profile + detected gender. */
export function recommendPromptStyles(gender, hairProfile, limit = 6) {
  const g = String(gender || '').toLowerCase();
  const isMen = g === 'men' || g === 'male';
  if (!g) return [];
  const view = headViewFromProfile(hairProfile);
  let pool = getPromptStylesForGender(g);
  if (view) pool = filterPromptStylesForHeadView(pool, view);
  const ranked = [...pool].sort(
    (a, b) => scorePromptStyle(b, hairProfile, isMen) - scorePromptStyle(a, hairProfile, isMen),
  );
  const top = ranked.slice(0, Math.max(1, Math.min(limit, 8)));
  return top.map((style) => ({
    type: 'hairstyle',
    name: style.name,
    style_id: style.id,
    prompt: style.prompt,
    description: style.prompt,
    thumbnail_url: style.thumbnail_url || localHairStyleThumbPath(style.id),
    reason: reasonForStyle(style, hairProfile),
    categories: [style.category],
  }));
}
